import React, {useMemo} from "react";
import dayjs from "dayjs";
import {Box, Card, Chip, Tooltip, Divider, IconButton} from "@mui/material";

import VisibilityIcon from "@mui/icons-material/Visibility";

import {useNavigate} from "react-router-dom";
import MDTypography from "components/MDTypography";
import properties from "constants/ApplicationConstants";

const formatDate = date => (date ? dayjs(date).format("DD MMM YYYY") : "N/A");

const UpcomingWorkAnniversary = ({
  workAnniversaryData = [],
  milestoneYears = 5,
  title = "Upcoming 5 Year Work Anniversaries"
}) => {
  const navigate = useNavigate();

  const upcomingEmployees = useMemo(() => {
    return [...workAnniversaryData].sort(
      (a, b) => dayjs(a.workAnniversaryDate).valueOf() - dayjs(b.workAnniversaryDate).valueOf()
    );
  }, [workAnniversaryData]);

  const getCompletedService = dateOfJoining => {
    if (!dateOfJoining) return "N/A";

    const joiningDate = dayjs(dateOfJoining);
    const today = dayjs();

    const years = today.diff(joiningDate, "year");
    const months = today.diff(joiningDate.add(years, "year"), "month");

    return `${years}.${months}`;
  };

  const commonHeaderStyles = {
    variant: "h6",
    fontFamily: properties.fontFamily,
    fontWeight: "medium",
    color: "#18367A",
    mb: 1,
    mt: 1,
    sx: {
      fontSize: "1rem",
      letterSpacing: "0.1px",
      borderBottom: "none",
      paddingBottom: "8px",
      marginBottom: "16px",
      display: "inline-block",
      fontFamily: properties.fontFamily,
      position: "relative",
      "&::after": {
        content: '""',
        position: "absolute",
        left: 0,
        bottom: 0,
        width: "100%",
        height: "3px",
        borderRadius: "999px",
        background: "linear-gradient(90deg, #53B0E0, #18367A)"
      }
    }
  };

  const commonNoDataStyles = {
    variant: "body2",
    color: "text.secondary",
    fontFamily: properties.fontFamily,
    sx: {
      textAlign: "center",
      mt: 4
    }
  };

  return (
    <Card
      sx={{
        height: 400,
        p: 2.5,
        borderRadius: "16px",
        background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
        border: "1px solid rgba(24, 54, 122, 0.08)",
        boxShadow: "0 6px 24px rgba(24, 54, 122, 0.10)",
        display: "flex",
        flexDirection: "column",
        transition: "transform 0.25s ease, box-shadow 0.25s ease",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: "0 12px 32px rgba(24, 54, 122, 0.14)"
        }
      }}
    >
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" alignItems="center" gap={1}>
          <MDTypography
            variant="h6"
            sx={{
              mt: 1,
              fontFamily: properties.fontFamily,
              fontWeight: "medium",
              color: "#18367A !important",
              fontSize: "1rem",
              letterSpacing: "0.1px",
              borderBottom: "none",
              paddingBottom: "8px",
              marginBottom: "16px",
              display: "inline-block",
              position: "relative",
              "&::after": {
                content: '""',
                position: "absolute",
                left: 0,
                bottom: 0,
                width: "100%",
                height: "3px",
                borderRadius: "999px",
                background: "linear-gradient(90deg, #53B0E0, #18367A)"
              }
            }}
          >
            {title}
          </MDTypography>
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          <Chip
            label={upcomingEmployees.length}
            size="small"
            sx={{
              background: "linear-gradient(135deg, #53B0E0, #18367A)",
              color: "#ffffff",
              fontWeight: 600,
              fontFamily: properties.fontFamily,
              boxShadow: "0 2px 8px rgba(24, 54, 122, 0.25)"
            }}
          />

          <Tooltip title="View Completed Work Anniversaries">
            <IconButton
              size="small"
              onClick={() => navigate("/work-anniversary")}
              sx={{
                color: "#18367A",
                border: "1px solid rgba(24, 54, 122, 0.12)",
                "&:hover": {
                  backgroundColor: "rgba(24, 54, 122, 0.08)"
                }
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Divider sx={{my: 1.5}} />

      {/* Upcoming Employees */}
      <Box
        sx={{
          height: "calc(100% - 60px)",
          overflowY: "auto",
          overflowX: "hidden",
          pr: 1,
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(24, 54, 122, 0.35) transparent",
          "&::-webkit-scrollbar": {width: "4px"},
          "&::-webkit-scrollbar-track": {background: "transparent"},
          "&::-webkit-scrollbar-thumb": {
            background: "linear-gradient(180deg, #53B0E0, #18367A)",
            borderRadius: "10px"
          },
          "&::-webkit-scrollbar-button": {display: "none", height: 0, width: 0}
        }}
      >
        {upcomingEmployees.length > 0 ? (
          upcomingEmployees.map(item => (
            <Box
              key={item.empId}
              sx={{
                p: 1.75,
                mb: 1.2,
                borderRadius: "12px",
                backgroundColor: "#ffffff",
                border: "1px solid rgba(24, 54, 122, 0.06)",
                boxShadow: "0 1px 4px rgba(24, 54, 122, 0.05)",
                transition: "all 0.2s ease",
                "&:hover": {
                  transform: "translateY(-1px)",
                  borderColor: "rgba(24, 54, 122, 0.18)",
                  backgroundColor: "rgba(24, 54, 122, 0.03)",
                  boxShadow: "0 4px 12px rgba(24, 54, 122, 0.08)"
                }
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                <Box sx={{minWidth: 0, lineHeight: 0.7}}>
                  <MDTypography
                    variant="subtitle2"
                    fontWeight="medium"
                    sx={{
                      color: "#18367A",
                      fontFamily: properties.fontFamily,
                      mb: 0.5
                    }}
                  >
                    {item?.empName || "N/A"}
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      fontSize: "0.85rem",
                      fontFamily: properties.fontFamily
                    }}
                  >
                    {item?.designation ? item.designation.replace(/_/g, " ") : "N/A"}
                  </MDTypography>
                </Box>

                <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.75}>
                  <Box
                    sx={{
                      backgroundColor: "rgba(24, 54, 122, 0.08)",
                      padding: "4px 10px",
                      borderRadius: "18px",
                      border: "1px solid rgba(24, 54, 122, 0.08)",
                      display: "inline-flex",
                      alignItems: "center",
                      flexShrink: 0
                    }}
                  >
                    <MDTypography
                      variant="caption"
                      sx={{
                        color: "#18367A",
                        fontFamily: properties.fontFamily,
                        fontWeight: 500,
                        fontSize: "0.82rem",
                        lineHeight: 1.3
                      }}
                    >
                      {formatDate(item?.workAnniversaryDate)}
                    </MDTypography>
                  </Box>

                  <Chip
                    size="small"
                    label={getCompletedService(item?.dateOfJoining)}
                    sx={{
                      backgroundColor: "rgba(24, 54, 122, 0.08)",
                      color: "#18367A",
                      fontWeight: 500,
                      fontFamily: properties.fontFamily,
                      flexShrink: 0,
                      border: "1px solid rgba(24, 54, 122, 0.08)",
                      height: 26,
                      fontSize: "0.82rem",
                      "& .MuiChip-label": {px: 1}
                    }}
                  />
                </Box>
              </Box>
            </Box>
          ))
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" height="80%">
            <MDTypography
              sx={{
                mt: 1,
                fontFamily: properties.fontFamily,
                fontWeight: "medium",
                color: "#18367A !important",
                fontSize: "1rem",
                letterSpacing: "0.1px",
                paddingBottom: "8px",
                marginBottom: "16px"
              }}
            >
              {`No upcoming ${milestoneYears} year work anniversaries this year.`}
            </MDTypography>
          </Box>
        )}
      </Box>
    </Card>
  );
};

export default UpcomingWorkAnniversary;
